import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ConfigProvider, useAppConfig } from './contexts/ConfigContext';
import { ConversationProvider, useConversation } from './contexts/ConversationContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { ConnectionStatusComponent } from './components/ConnectionStatus/ConnectionStatus';
import { useErrorMessages } from './hooks/useErrorMessages';
import ConversationInterface from './components/ConversationInterface';
import appConfig from './config';
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const AppContent: React.FC = () => {
  const { config, loading, error } = useAppConfig();
  const { getFriendlyError } = useErrorMessages();

  // Show loading state until config is fetched
  if (loading || !config) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Starting your AI Assistant...</p>
        </div>
      </div>
    );
  }

  // If config fetch failed, create fallback config and show friendly error
  if (error) {
    const friendlyErrorMessage = getFriendlyError(error);
    
    // Fallback configuration - allows app to run with default settings
    const fallbackConfig = {
      api_url: appConfig.apiUrl,
      websocket_url: appConfig.websocketUrl,
      ui_theme: {
        app_background: '#f5f5f5',
        primary_color: '#1976d2',
        secondary_color: '#dc004e'
      },
      features: {
        enable_audio: process.env.REACT_APP_ENABLE_AUDIO === 'true',
        enable_text: process.env.REACT_APP_ENABLE_TEXT === 'true',
        enable_tts: process.env.REACT_APP_ENABLE_TTS === 'true'
      }
    };

    console.warn('Using fallback configuration due to error:', error);

    return (
      <ConversationProvider>        <FeedbackProvider>
          <div className="App" style={{ background: fallbackConfig.ui_theme.app_background }}>
            <ConnectionStatusComponent
              status="disconnected"
              error={friendlyErrorMessage}
              onRetry={() => window.location.reload()}
              canRetry={true}
              showWhenConnected={false}
            />
            <ConversationInterface />
          </div>
        </FeedbackProvider>
      </ConversationProvider>
    );
  }

  // Parse background - support single color or gradient
  const parseBackground = (background: string) => {
    if (background.includes(',')) {
      const colors = background.split(',');
      return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
    }
    return background;
  };

  const dynamicStyles = {
    background: parseBackground(config.ui_theme?.app_background || '#f5f5f5')
  };  // Only render ConversationProvider when config is ready
  return (
    <ConversationProvider>
      <FeedbackProvider>
        <div className="App" style={dynamicStyles}>
          <MainAppContent />
        </div>
      </FeedbackProvider>
    </ConversationProvider>
  );
};

const MainAppContent: React.FC = () => {
  const { 
    connectionStatus, 
    error, 
    retryConnection, 
    isRetrying, 
    retryAttemptCount, 
    nextRetryIn, 
    canRetry,
    friendlyError 
  } = useConversation();

  const { getConnectionMessage } = useErrorMessages();
  return (
    <>
      <ConnectionStatusComponent
        status={connectionStatus}
        error={friendlyError || error}
        message={getConnectionMessage(connectionStatus, friendlyError || error)}
        onRetry={retryConnection}
        isRetrying={isRetrying}
        attemptCount={retryAttemptCount}
        nextRetryIn={nextRetryIn}
        canRetry={canRetry}
        showWhenConnected={false}
      />
      <ConversationInterface />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConfigProvider>
        <AppContent />
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default App;
